import { Link } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { LoginArea } from '@/components/auth/LoginArea';
import { SAMPLE_PUZZLES, encodePuzzle, Cell, type PuzzleData } from '@/lib/puzzle';

function MiniGrid({ puzzle }: { puzzle: PuzzleData }) {
  return (
    <div
      className="grid gap-0.5 mx-auto"
      style={{
        gridTemplateColumns: `repeat(${puzzle.width}, minmax(0, 1fr))`,
        maxWidth: 160,
      }}
    >
      {puzzle.cells.map((cell, i) => {
        const x = i % puzzle.width;
        const y = Math.floor(i / puzzle.width);
        const isStart = x === puzzle.startX && y === puzzle.startY;
        if (cell === Cell.Wall) {
          return <div key={i} className="aspect-square rounded-sm bg-gray-800 dark:bg-gray-950" />;
        }
        if (cell === Cell.Passive) {
          return <div key={i} className="aspect-square rounded-sm bg-gray-300 dark:bg-gray-600" />;
        }
        if (isStart) {
          return <div key={i} className="aspect-square rounded-sm bg-yellow-400" />;
        }
        return <div key={i} className="aspect-square rounded-sm bg-sky-300 dark:bg-sky-600" />;
      })}
    </div>
  );
}

export default function Index() {
  useSeoMeta({
    title: 'Somaria Tiles — Hamilton Path Puzzle',
    description: 'A tile puzzle game inspired by The Legend of Zelda: Oracle of Ages. Create, share, and solve puzzles on Nostr.',
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      {/* Nav */}
      <header className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="size-7 rounded-lg bg-gradient-to-br from-sky-400 to-red-500" />
            <span className="font-bold text-lg">Somaria Tiles</span>
          </div>
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm">
              <Link to="/browse">Browse</Link>
            </Button>
            <Button asChild variant="ghost" size="sm">
              <Link to="/editor">Editor</Link>
            </Button>
            <LoginArea />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300 text-sm font-medium mb-6">
          <span className="size-2 rounded-full bg-sky-500 animate-pulse" />
          Inspired by Zelda: Oracle of Ages
        </div>
        <h1 className="text-5xl font-bold tracking-tight mb-4">
          Somaria Tiles
        </h1>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
          Step across a grid of tiles, turning every blue tile red. Use the Cane of Somaria
          to create obstacles. Walk over grey tiles without activating them. Solve puzzles,
          build your own, and share them on Nostr.
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Button asChild size="lg">
            <Link to="/browse">Play Now</Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/editor">Create a Puzzle</Link>
          </Button>
          <Button asChild size="lg" variant="outline">
            <Link to="/browse">Browse Puzzles</Link>
          </Button>
        </div>
      </section>

      {/* How to play */}
      <section className="max-w-5xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-center mb-8">How to Play</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-lg bg-sky-300 dark:bg-sky-600 flex items-center justify-center text-white font-bold">
                  1
                </div>
                <h3 className="font-semibold">Step</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Press a direction to turn the character to face that way. Press
                the same direction again to step one tile. Step from the yellow
                tile onto a blue tile to activate it. Return to the yellow tile
                to continue solving.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-lg bg-red-400 dark:bg-red-600 flex items-center justify-center text-white font-bold">
                  2
                </div>
                <h3 className="font-semibold">Cover All</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                The yellow tile marks where you must be to activate a new tile.
                Step from it onto a blue tile to turn it red — the yellow tile
                moves there. Walk freely on red and grey tiles, but return to
                the yellow tile to keep solving!
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-lg bg-purple-500 flex items-center justify-center text-white font-bold">
                  3
                </div>
                <h3 className="font-semibold">Cane of Somaria</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Some puzzles include the Cane of Somaria. Press Space to create an obstacle
                in front of you, reducing the tiles you need to cover. Walk into a block to
                push it!
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="size-10 rounded-lg bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-white font-bold">
                  4
                </div>
                <h3 className="font-semibold">Grey Tiles</h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Light grey tiles can be walked on like any other tile, but they
                never turn red. Use them as passages — they don't count toward
                the goal.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sample puzzles */}
      <section className="max-w-5xl mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-center mb-2">Try a Puzzle</h2>
        <p className="text-sm text-muted-foreground text-center mb-8">
          Start with these built-in puzzles to learn the mechanics.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAMPLE_PUZZLES.map((puzzle) => (
            <Card key={puzzle.title} className="hover:shadow-lg transition-shadow">
              <Link to={`/play?p=${encodePuzzle(puzzle)}`}>
                <div className="bg-slate-100 dark:bg-slate-800 p-4 rounded-t-xl">
                  <MiniGrid puzzle={puzzle} />
                </div>
              </Link>
              <CardHeader>
                <CardTitle className="text-lg">{puzzle.title}</CardTitle>
                <CardDescription className="line-clamp-2">{puzzle.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {puzzle.width}{'\u00d7'}{puzzle.height} grid
                  </span>
                  {puzzle.somaria && (
                    <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                      Cane of Somaria
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t mt-8">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Vibed with{' '}
            <a
              href="https://marlowe.shakespeare.wtf"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-foreground hover:underline"
            >
              Marlowe
            </a>
          </p>
          <p className="text-sm text-muted-foreground">
            Puzzles shared via{' '}
            <a
              href="https://nostr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-foreground hover:underline"
            >
              Nostr
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
